import { useMemo, useRef, useState, type FormEvent } from 'react'
import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { newId, type CuttingMethod } from '@gk/contracts'
import { useClub, useEngineState } from '../club/ClubProvider.tsx'
import {
  CUTTING_METHODS, CUTTING_METHOD_KEY, CUTTING_PROBLEM_KEY, buildCuttingPayload, cuttingFormProblem, cuttingMethodKey, machineSetups, setupsOf, useAssets, useCuttingSetups, useMounts,
  type AssetRow, type CuttingForm, type CuttingSetup,
} from '../data/fleet.ts'
import { fmtNum } from '../lib/format.ts'
import { formatLocal, localToInstant } from '../lib/time.ts'
import type { Pending } from '../sync/merge.ts'
import { Banner, DisabledHint, Field, Pill, Section } from '../ui/components.tsx'
import { useAction, useWriteAccess } from '../ui/hooks.ts'
import { usePersonNames } from './measures/shared.tsx'
import { FoldSummary } from './workshop/shared.tsx'

/** M16-J: "Réglage de coupe" on a machine's or a unit's page (GK-013). The height a person MEASURED, with the method and the moment, beside the job's own target
 *  when he picked one (copied, never computed): the last one in large type, the history under a fold, a 56-px form with the decimal keypad. Recording is a
 *  field fact (offline, shown at once, pending until the server's row replaces it); the server alone refuses (a retired machine, the future, a third decimal).
 *  Shown for every asset: the club decides where it measures (per unit or per machine) and how (the method is a field, not a question). */
export function CuttingSetupPanel({ asset }: { asset: Pending<AssetRow> }) {
  const { t } = useTranslation()
  const rows = useCuttingSetups()
  const access = useWriteAccess('setup.record')
  const history = useMemo(() => (rows ? setupsOf(rows, asset.id) : []), [rows, asset.id])
  if (!rows) return null
  const last = history[0]
  const inactive = asset.lifecycle != null && asset.lifecycle !== 'active'
  return (
    <div id="cutting" className="scroll-mt-20">
      <Section title={t('cutting.title')} help={t('cutting.help')} testId="cutting-panel">
        <div className="card flex flex-col gap-3">
          {last ? <LastSetup setup={last} /> : <p className="opacity-70" data-testid="cutting-none">{t('cutting.none')}</p>}
          {access.permitted && !inactive && !asset._pending && <RecordForm asset={asset} />}
          {access.permitted && inactive && <p className="meta" data-testid="cutting-retired">{t('cutting.retired')}</p>}
          {history.length > 0 && <History rows={history} />}
        </div>
      </Section>
    </div>
  )
}

const Mm = ({ mm, className = '' }: { mm: number; className?: string }) => {
  const { t, i18n } = useTranslation()
  return <span className={className}>{t('cutting.mm', { mm: fmtNum(mm, i18n.language) })}</span>
}
/** The method's word from the catalogue; a word this build does not know (a newer build's) shows as it is. */
function MethodWord({ method }: { method: string }) {
  const { t } = useTranslation()
  const key = cuttingMethodKey(method)
  return <>{key ? t(key) : method}</>
}
function SetupMeta({ setup }: { setup: CuttingSetup }) {
  const { t, i18n } = useTranslation()
  const { site } = useClub()
  const name = usePersonNames()(setup.actor_person_id)
  return (
    <p className="meta">
      {t('cutting.measuredAt', { when: formatLocal(setup.measured_at, site.timezone, 'dd/MM/yyyy HH:mm') })} · <MethodWord method={setup.method} />
      {setup.target_mm != null && <> · {t('cutting.target', { mm: fmtNum(setup.target_mm, i18n.language) })}</>}
      {name && <> · {t('cutting.by', { name })}</>}
    </p>
  )
}
function LastSetup({ setup }: { setup: CuttingSetup }) {
  const { t } = useTranslation()
  return (
    <div className="flex flex-col gap-1" data-testid="cutting-last" data-setup={setup.id}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm font-semibold opacity-70">{t('cutting.last')}</p>
        {setup._pending && <Pill tone="amber">{t('cutting.pending')}</Pill>}
      </div>
      <p className="text-3xl font-bold" data-testid="cutting-last-mm"><Mm mm={setup.measured_mm} /></p>
      <SetupMeta setup={setup} />
      {setup.note && <p className="text-sm">{setup.note}</p>}
    </div>
  )
}

// ---------------------------------------------------------------------------------------------------- recording (a field fact)
function RecordForm({ asset }: { asset: Pending<AssetRow> }) {
  const { t } = useTranslation()
  const { send, site } = useClub()
  const engine = useEngineState()
  const access = useWriteAccess('setup.record')
  const { busy, error, run } = useAction()
  const id = useRef(newId())
  const [f, setF] = useState<CuttingForm>({ measured: '', method: 'gauge', target: '', note: '' })
  const [when, setWhen] = useState('')
  const [done, setDone] = useState<string | null>(null)
  const problem = cuttingFormProblem(f)
  function submit(e: FormEvent) {
    e.preventDefault(); setDone(null)
    void run(async () => {
      const at = when ? localToInstant(when.slice(0, 10), when.slice(11, 16), site.timezone) : undefined
      await send('cutting_setup.record@1', buildCuttingPayload(id.current, asset.id, { ...f, ...(at ? { measuredAt: at } : {}) }))
      id.current = newId(); setF({ measured: '', method: f.method, target: '', note: '' }); setWhen('')
      setDone(engine.online === false || !navigator.onLine ? t('cutting.recordedOffline') : t('cutting.recorded'))
    })
  }
  return (
    <form className="flex flex-col gap-3 border-t pt-3" onSubmit={submit} data-testid="cutting-form">
      <h3 className="text-lg font-bold">{t('cutting.record')}</h3>
      <Field label={t('cutting.measured')} hint={t('cutting.measuredHint')} htmlFor="cutting-mm">
        <input id="cutting-mm" className="input" inputMode="decimal" autoComplete="off" value={f.measured} onChange={(e) => { setF({ ...f, measured: e.target.value }); setDone(null) }} data-testid="cutting-mm" />
      </Field>
      <Field label={t('cutting.methodLabel')} htmlFor="cutting-method">
        <select id="cutting-method" className="input" value={f.method} onChange={(e) => setF({ ...f, method: e.target.value as CuttingMethod })} data-testid="cutting-method">
          {CUTTING_METHODS.map((m) => <option key={m} value={m}>{t(CUTTING_METHOD_KEY[m])}</option>)}
        </select>
      </Field>
      <Field label={t('cutting.targetLabel')} hint={t('cutting.targetHint')} htmlFor="cutting-target">
        <input id="cutting-target" className="input" inputMode="decimal" autoComplete="off" value={f.target} onChange={(e) => setF({ ...f, target: e.target.value })} data-testid="cutting-target" />
      </Field>
      <Field label={t('cutting.note')} hint={t('cutting.noteHint')} htmlFor="cutting-note">
        <input id="cutting-note" className="input" maxLength={300} value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} data-testid="cutting-note" />
      </Field>
      <details className="group">
        <FoldSummary>{t('cutting.when')}</FoldSummary>
        <Field label={t('cutting.when')} hint={t('cutting.whenHint')} htmlFor="cutting-when">
          <input id="cutting-when" className="input" type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} data-testid="cutting-when" />
        </Field>
      </details>
      {error && <Banner tone="error">{error}</Banner>}
      {done && <Banner tone="ok" testId="cutting-done">{done}</Banner>}
      <DisabledHint show={!!problem && !access.readOnlyMode} testId="cutting-hint">{problem ? t(CUTTING_PROBLEM_KEY[problem]) : ''}</DisabledHint>
      <button className="btn btn-primary" disabled={busy || access.readOnlyMode || !!problem} data-testid="cutting-save">{t('cutting.recordGo')}</button>
    </form>
  )
}

// ---------------------------------------------------------------------------------------------------- the history
function History({ rows }: { rows: CuttingSetup[] }) {
  const { t } = useTranslation()
  return (
    <details className="group border-t pt-3" data-testid="cutting-history">
      <FoldSummary>{t('cutting.history')} ({rows.length})</FoldSummary>
      <ul className="mt-2 flex flex-col gap-2">
        {rows.map((s) => (
          <li key={s.id} className="rounded-xl border-2 border-slate-300 p-3 dark:border-slate-600" data-testid="cutting-row" data-setup={s.id}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="text-lg font-bold"><Mm mm={s.measured_mm} /></span>
              {s._pending && <Pill tone="amber">{t('cutting.pending')}</Pill>}
            </div>
            <SetupMeta setup={s} />
            {s.note && <p className="text-sm">{s.note}</p>}
            {s.job_id && <Link to="/plan/job/$jobId" params={{ jobId: s.job_id }} className="btn mt-2 w-full" data-testid="cutting-job">{t('cutting.job')}</Link>}
          </li>
        ))}
      </ul>
    </details>
  )
}

/** The timer's line (Work.tsx, the machine's card): "réglée à 3,2 mm le … · Jauge" for the machine and for each unit mounted on it at this moment (the mount
 *  walk of data/fleet.ts), nothing when nothing was measured. The job's own target is in the job's settings above; nothing here compares the two. */
export function MachineSetupLines({ machineId }: { machineId: string }) {
  const { t, i18n } = useTranslation()
  const { site } = useClub()
  const setups = useCuttingSetups(); const mounts = useMounts(); const assets = useAssets()
  const [at] = useState(() => new Date())
  const lines = useMemo(() => (setups && mounts ? machineSetups(setups, mounts, machineId, at) : []), [setups, mounts, machineId, at])
  if (lines.length === 0) return null
  const tagOf = (id: string) => assets?.find((a) => a.id === id)?.readable_tag ?? '…'
  return (
    <div className="flex flex-col gap-1" data-testid="work-setup">
      <ul className="flex flex-col gap-1">
        {lines.map(({ assetId, setup }) => {
          const method = cuttingMethodKey(setup.method)
          const args = { mm: fmtNum(setup.measured_mm, i18n.language), when: formatLocal(setup.measured_at, site.timezone, 'dd/MM/yyyy HH:mm'), method: method ? t(method) : setup.method, tag: tagOf(assetId) }
          return (
            <li key={assetId} className="flex flex-wrap items-center gap-2" data-testid="work-setup-line" data-asset={assetId}>
              <span>{assetId === machineId ? t('cutting.onTimer', args) : t('cutting.onTimerUnit', args)}</span>
              {setup._pending && <Pill tone="amber">{t('cutting.pending')}</Pill>}
            </li>
          )
        })}
      </ul>
      <p className="meta">{t('cutting.onTimerHelp')}</p>
    </div>
  )
}
